import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  meApi,
  pairingsApi,
  type BootTokenCreated,
  type PendingPairingLookup,
} from '@/api/client';
import { useFeature } from '@/hooks/useFeature';

export interface PairWaitingServerModalProps {
  open: boolean;
  siteId: string;
  onClose: () => void;
  /** Called with the freshly-minted boot token once the operator clicks
   *  Pair. The parent typically opens the AddNodeWizard with this token
   *  pre-loaded (Phase 5d's `preMintedToken` prop) so the operator drops
   *  straight into the Live progress view. */
  onPaired: (token: BootTokenCreated) => void;
}

/** Mask user input as XXX-YYY, uppercasing letters and stripping
 *  separators / invalid chars. The pairing-code alphabet is
 *  base32-no-confusables — frontend doesn't enforce the exact alphabet
 *  (backend rejects anyway) but does enforce the 3-3 hyphen shape so
 *  the operator can't accidentally send "abcdef" or "abc-de-f". */
function maskCode(raw: string): string {
  const cleaned = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 6);
  if (cleaned.length <= 3) return cleaned;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
}

function relAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.max(0, Math.floor(ms / 1000))}s ago`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3600_000)}h ago`;
}

/**
 * Phase 5d — operator-facing modal for the device-flow pairing UX.
 *
 * Operator at the rack sees a 6-char code displayed on the BIOS
 * console (returned by `/b/{slug}/pair`). They open this modal, type
 * the code, and:
 *   1. **Look up** — fetches the `pending_pairings` row via
 *      `pairingsApi.getByCode()` and shows observed MAC + UUID + age
 *      so the operator can confirm they're pairing the right machine.
 *   2. **Pair** — calls `pairingsApi.attach()` which mints a token
 *      bound to (siteId, observed MAC/UUID) and attaches it. The
 *      backend returns the same shape as `POST /tokens`. We hand it
 *      back to the parent via `onPaired` so it can open the wizard
 *      in post-pair mode.
 *
 * Behind the `netboot` feature flag, off by default (#954): it never opens,
 * and never attaches (mints), while the flag is off for this site.
 */
export default function PairWaitingServerModal(props: PairWaitingServerModalProps): JSX.Element | null {
  const netboot = useFeature('netboot', { siteId: props.siteId });
  if (!netboot) return null;
  return <PairWaitingServerModalBody {...props} />;
}

function PairWaitingServerModalBody({
  open,
  siteId,
  onClose,
  onPaired,
}: PairWaitingServerModalProps): JSX.Element | null {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCode('');
    setError(null);
  }, [open]);

  const { data: org } = useQuery({
    queryKey: ['me', 'organization'],
    queryFn: () => meApi.organization(),
    enabled: open,
    staleTime: 60_000,
  });
  const slug = org?.slug;

  // Look-up is on-demand: operator clicks Look up to confirm before
  // committing to Pair. Using react-query with `enabled: false` so we
  // can fire it via refetch().
  const lookup = useQuery<PendingPairingLookup, Error>({
    queryKey: ['pairing-lookup', slug, code],
    queryFn: () => pairingsApi.getByCode(slug!, code),
    enabled: false,
    retry: false,
  });

  const isValidCode = useMemo(() => /^[A-Z0-9]{3}-[A-Z0-9]{3}$/.test(code), [code]);
  const canLookup = open && !!slug && isValidCode;

  const attachMutation = useMutation<BootTokenCreated, Error, void>({
    mutationFn: async () => {
      if (!slug) throw new Error('Org slug not loaded');
      return pairingsApi.attach(slug, code, { siteId });
    },
    onSuccess: (token) => {
      onPaired(token);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  if (!open) return null;

  const looked = lookup.data;
  const lookupError = lookup.error;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-surface border border-surface-elevated rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h2 className="text-base font-semibold text-silver-bright">Pair waiting server</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-silver-muted hover:text-silver-bright transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-xs text-silver-muted">
            Enter the 6-character pairing code displayed on the new
            server&apos;s BIOS console. Look up the code to confirm
            you&apos;re pairing the right machine before you click Pair.
          </p>

          <div className="space-y-1.5">
            <label htmlFor="pair-code" className="block text-xs text-silver-muted">
              Pairing code
            </label>
            <input
              id="pair-code"
              type="text"
              value={code}
              onChange={(e) => {
                setCode(maskCode(e.target.value));
                setError(null);
                // queryKey includes `code` so lookup.data clears as the
                // user types (react-query v5 removed the old `.remove()`
                // API; the per-key cache is sufficient).
              }}
              placeholder="ABC-DEF"
              autoFocus
              className="w-full bg-surface-deep border border-surface-elevated rounded px-3 py-2 text-sm font-mono text-silver-bright text-center tracking-widest placeholder:text-silver-muted/40 focus:border-gold focus:outline-none uppercase"
              maxLength={7}
            />
          </div>

          {looked && !lookupError && (
            <div className="bg-surface-deep/50 border border-surface-elevated rounded-lg px-3 py-2 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-silver-muted">
                Observed at the server
              </div>
              <div className="text-xs text-silver-bright">
                MAC: <span className="font-mono">{looked.mac}</span>
              </div>
              {looked.uuid && (
                <div className="text-xs text-silver-bright">
                  UUID: <span className="font-mono">{looked.uuid}</span>
                </div>
              )}
              {looked.clientIp && (
                <div className="text-xs text-silver-bright">
                  Client IP: <span className="font-mono">{looked.clientIp}</span>
                </div>
              )}
              <div className="text-xs text-silver-muted">
                First seen: {relAge(looked.createdAt)}
                {looked.isExpired && <span className="text-status-error"> · expired</span>}
                {looked.isPaired && <span className="text-status-warning"> · already paired</span>}
              </div>
            </div>
          )}

          {lookupError && (
            <div className="rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
              Look-up failed: {lookupError.message}
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
              Pair failed: {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => lookup.refetch()}
            disabled={!canLookup || lookup.isFetching}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-surface-elevated text-silver-bright hover:bg-surface-elevated transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {lookup.isFetching ? 'Looking up…' : 'Look up'}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              attachMutation.mutate();
            }}
            disabled={!canLookup || attachMutation.isPending || (looked?.isExpired ?? false) || (looked?.isPaired ?? false)}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-gold text-surface-deep hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {attachMutation.isPending ? 'Pairing…' : 'Pair'}
          </button>
        </div>
      </div>
    </div>
  );
}
